import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { SiteSearchSelect } from '@/components/SiteSearchSelect';
import { LoadingBlock } from '@/components/PageHeader';
import { useSession } from '@/lib/auth/session';
import type { Site } from '@/lib/types';
import { compressToJpeg, DOCUMENT_PROFILE, parseExif } from '@/lib/photoMeta';
import { errorMessage, inputStyle, today } from '@/lib/format';

import { DEFAULT_FORMS } from './journalForm';
import { JournalPaper, type Approvals, type RequiredField } from './JournalPaper';
import { RiskSheet } from './RiskSheet';
import { blankRiskItem, SHEET_SIGNERS } from './sheetModel';
import { TbmSheet } from './TbmSheet';
import { DocSheet } from './docs/DocSheet';
import { isDocType } from './docs/types';
import { NameSuggestInput } from './NameSuggestInput';
import { EducationPicker } from './EducationPicker';
import { SHEET_STYLES } from './SheetGrid';
import { headCellOf } from './approvalCells';
import { PersonnelSearchInput } from '@/components/PersonnelSearchInput';
import { useDirectoryTree } from '@/lib/directory/useDirectory';
import { defaultChecks } from './patrolChecklist';
import {
  addSafeJournalPhoto,
  blankSignRoute,
  blankTbm,
  isInspection,
  isSheetType,
  downloadSafeJournal,
  educationLabel,
  educationSubsOf,
  fetchLatestSafeJournal,
  HQ,
  JOURNAL_TYPES,
  safeJournalKeys,
  uploadSafeJournalFile,
  useEducationCatalog,
  useHeadApproval,
  usePatrolTemplate,
  useSafeJournal,
  useSafeJournalPhotoWrite,
  useSafeJournalWrite,
  serverAddedFields,
  type Activity,
  type EducationCatalog,
  type ApprovalRole,
  type EducationKind,
  type EducationSub,
  type Facility,
  type HealthItem,
  type Journal,
  type JournalInput,
  type JournalPhoto,
  type JournalType,
  type PatrolKind,
  type PatrolNote,
  type PhotoSlot,
  type SignRoute,
  plainLabel,
} from './safeJournalApi';

/**
 * 일지 편집 창 (DOC-122). 버튼은 제목 줄에 둔다 — [PDF] · [삭제] · [저장].
 *
 * <p>버튼은 조건으로 감추지 않는다. 아직 저장하지 않은 일지에서 [PDF] · 사진 추가를 누르면 그 자리에서
 * 사유를 답한다(사진은 일지 id 에 붙으므로 저장이 먼저다). 권한이 없으면 서버가 답한다.
 *
 * <p>저장하면 창을 닫는다(사용자 지시 2026-10-23 「저장했으면 모달이 닫혀야지」). 저장 전에 고른 사진은 다 붙인 뒤에
 * 닫고, 사진이 하나라도 못 붙었으면 닫지 않고 저장된 일지로 바꿔 사유를 보여 준다.
 */
export function SafeJournalEditor({
  type,
  journalId,
  defaultSite,
  sites,
  allowHq = true,
  onClose,
}: {
  /** 새 일지의 처음 종류 — 보던 메뉴의 것. 창 안에서 바꿀 수 있다. 저장한 일지는 그 일지의 종류로 잠긴다. */
  type: JournalType;
  journalId: string | null;
  /** 목록의 현장 고르개 값 — 빈 글자(전체) · HQ · 현장 id. 새 일지의 기본값이다. */
  defaultSite: string;
  sites: Site[];
  /** 본사 일지를 고를 수 있나 — 안전 권한(전 현장)만. 배정 현장만 보는 사람에게는 자기 현장들만 보인다(DOC-136 §6). */
  allowHq?: boolean;
  onClose: () => void;
}) {
  const [id, setId] = useState<string | null>(journalId);
  const journal = useSafeJournal(id);
  const me = useSession().data?.user?.name ?? null;

  if (id && !journal.data) {
    return (
      <Modal title={JOURNAL_TYPES[type].label} onClose={onClose} maxWidth="1100px">
        <LoadingBlock />
      </Modal>
    );
  }

  return (
    <EditorForm
      // 저장해 id 를 받으면 서버 값으로 다시 채운다(빈 줄이 걸러지고 조치 상태가 정리된 모양).
      key={id ?? 'new'}
      journal={journal.data ?? null}
      initial={journal.data ? fromJournal(journal.data) : blank(type, defaultSite, me)}
      sites={sites}
      allowHq={allowHq}
      onSaved={setId}
      onClose={onClose}
    />
  );
}

/** 편집 중인 값. 현장은 고르개 값(빈 글자 = 안 고름 · HQ = 본사 · 현장 id)으로 든다. */
type Draft = Omit<JournalInput, 'siteId'> & { site: string };

/** 작성자는 쓰는 사람 이름이 기본값이다(비워 저장해도 서버가 같은 이름을 넣는다 — 미리 보여 줄 뿐). */
function blank(type: JournalType, defaultSite: string, me: string | null): Draft {
  return withBlankLine({
    journalType: type,
    site: defaultSite,
    journalDate: today(),
    title: null,
    weather: null,
    temperature: null,
    authorName: me,
    // 안전교육일지는 교육 종류를 먼저 고른다(§16) — 고르면 그 종류의 양식 · 기본값이 들어온다.
    educationKind: null,
    educationSubKind: null,
    educationMaterial: null,
    startTime: null,
    endTime: null,
    educationHours: null,
    instructor: null,
    target: null,
    headcount: null,
    place: null,
    inspector: null,
    workforce: null,
    content: null,
    specialNote: null,
    attendees: [],
    healthItems: [],
    findings: [],
    patrolNotes: [],
    activities: [],
    facilities: [],
    // 새 순회점검일지는 기본 점검표로 시작한다(patrolChecklist.ts).
    checks: isInspection(type) ? defaultChecks() : [],
    tbm: type === 'HEALTH' ? blankTbm() : null,
    rowLayout: null,
    signRoute: blankSignRoute(),
    vendorName: null,
    workItems: [],
    attendeeNames: null,
    riskItems: type === 'RISK' ? [blankRiskItem()] : [],
  });
}

function fromJournal(journal: Journal): Draft {
  return withBlankLine({
    journalType: journal.journalType,
    site: journal.siteId ?? HQ,
    journalDate: journal.journalDate,
    title: journal.title,
    weather: journal.weather,
    temperature: journal.temperature,
    authorName: journal.authorName,
    educationKind: journal.educationKind,
    startTime: journal.startTime,
    endTime: journal.endTime,
    educationHours: journal.educationHours,
    instructor: journal.instructor,
    place: journal.place,
    inspector: journal.inspector,
    workforce: journal.workforce,
    content: journal.content,
    specialNote: journal.specialNote,
    attendees: journal.attendees,
    healthItems: journal.healthItems,
    findings: journal.findings,
    ...serverAddedFields(journal),
    signRoute: journal.signRoute ?? blankSignRoute(),
  });
}

/**
 * 지난 일지에서 이어 쓸 것만 옮긴다 — 화면에 있는 칸만. 현장 · 날짜는 지금 것이고, 사진은 그날의 것이라
 * 옮기지 않는다. 줄은 id 를 떼어 새 줄로 저장한다(지난 일지의 줄은 그대로 남는다).
 *
 * <ul>
 *   <li>교육 — 교육구분 · 교육내용 · 시간 · 대상 · 인원 · 장소 · 강사</li>
 *   <li>안전보건 — 위험요인 · 안전조치 줄(이행 체크는 푼다). Patrol · 활동 · 시설물은 그날의 기록이라 안 옮긴다.</li>
 *   <li>순회점검 — 점검자와 점검표 항목(결과는 비운다 — 사람이 더한 항목이 이어진다).</li>
 * </ul>
 */
function carryOver(current: Draft, previous: Journal): Draft {
  const prev = serverAddedFields(previous);
  return withBlankLine({
    ...current,
    educationKind: previous.educationKind ?? current.educationKind,
    educationSubKind: previous.educationKind ? prev.educationSubKind : current.educationSubKind,
    startTime: previous.startTime,
    endTime: previous.endTime,
    educationHours: previous.educationHours,
    instructor: previous.instructor,
    target: prev.target,
    headcount: prev.headcount,
    place: previous.place,
    inspector: previous.inspector,
    content: previous.journalType === 'HEALTH' ? current.content : previous.content,
    healthItems: previous.healthItems.map((row) => ({ ...row, id: null, checked: false })),
    checks: current.checks.map((row) => ({ ...row, result: null })),
    // 위험성평가는 평가 구분 · 줄을, TBM 일지는 업체 · 작업종류 · 참석자 · 체크리스트(미조치로 되돌려)를 잇는다(V572).
    title: previous.journalType === 'RISK' ? previous.title : current.title,
    vendorName: prev.vendorName,
    workItems: prev.workItems,
    attendeeNames: prev.attendeeNames,
    riskItems: (prev.riskItems ?? []).map((row) => ({ ...row, id: null })),
    findings:
      previous.journalType === 'TBM'
        ? previous.findings.map((row) => ({ ...row, id: null, status: 'OPEN' as const, doneDate: null, dueDate: current.journalDate }))
        : current.findings,
  });
}

/** 그 종류의 표가 비었으면 빈 줄 하나를 세워 둔다 — 바로 적거나 붙여 넣을 자리. 빈 줄은 서버가 버린다. */
function withBlankLine(draft: Draft): Draft {
  if (draft.journalType === 'HEALTH') {
    const kinds: PatrolKind[] = ['HEAD', 'SAFE'];
    const missing = kinds.filter((kind) => !draft.patrolNotes.some((row) => row.patrolKind === kind));
    return {
      ...draft,
      healthItems: draft.healthItems.length === 0 ? [blankHealthItem()] : draft.healthItems,
      patrolNotes: missing.length === 0 ? draft.patrolNotes : sortNotes([...draft.patrolNotes, ...missing.map(blankPatrolNote)]),
      activities: draft.activities.length === 0 ? [blankActivity()] : draft.activities,
      facilities: draft.facilities.length === 0 ? [blankFacility()] : draft.facilities,
    };
  }
  return draft;
}

/** 소장 Patrol 줄이 먼저, 안전 Patrol 줄이 뒤 — 두 표가 한 목록을 나눠 쓴다. */
function sortNotes(rows: PatrolNote[]): PatrolNote[] {
  return [...rows.filter((row) => row.patrolKind === 'HEAD'), ...rows.filter((row) => row.patrolKind === 'SAFE')];
}

function blankPatrolNote(patrolKind: PatrolKind): PatrolNote {
  return { patrolKind, inspector: null, instruction: null, result: null };
}

function blankActivity(): Activity {
  return { activity: null, meeting: null };
}

function blankFacility(): Facility {
  return { facilityKind: null, location: null, workers: null, detail: null };
}

function blankHealthItem(): HealthItem {
  return { workContent: null, location: null, hazard: null, measure: null, checked: false, note: null };
}

const NO_FIELDS: ReadonlySet<RequiredField> = new Set();

/** 비어 있는 입력칸(현장 · 날짜) — 포커스 링과 같은 꼴의 옅은 붉은 링. 종이 칸의 표시({@code JournalPaper})와 같은 색. */
const MISSING_INPUT: CSSProperties = {
  borderColor: '#f87171',
  boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.18)',
  background: '#fffafa',
};

/**
 * 비어 있는 [필수] 칸 — 안전교육일지의 기존 사이트 화면 필수 칸(교육일시 · 교육시간 · 대상 · 인원 · 장소 · 강사).
 * 서버는 대상 · 인원을 모르는 옛 앱도 받아야 해서 막지 않는다. 교육 종류는 고르기 전에는 종이가 열리지 않는다.
 */
function missingFields(draft: Draft): ReadonlySet<RequiredField> {
  if (draft.journalType !== 'EDUCATION') return NO_FIELDS;
  const blanks = new Set<RequiredField>();
  if (!draft.journalDate) blanks.add('journalDate');
  if (draft.educationHours === null) blanks.add('educationHours');
  if (!draft.target?.trim()) blanks.add('target');
  if (draft.headcount === null) blanks.add('headcount');
  if (!draft.place?.trim()) blanks.add('place');
  if (!draft.instructor?.trim()) blanks.add('instructor');
  return blanks;
}

/** 고른 교육 하나(카탈로그). 없으면 null. */
function educationSubOf(catalog: EducationCatalog | undefined, kind: string | null, subKind: string | null) {
  return educationSubsOf(catalog, kind).find((one) => one.code === subKind) ?? null;
}

const full: CSSProperties = { ...inputStyle };
/** 줄 표 · 특이사항 칸 — 한 창에 다 들어가도록 작게. */
const cellInput: CSSProperties = { ...inputStyle, padding: '4px 6px', fontSize: '12px' };

function EditorForm({
  journal,
  initial,
  sites,
  allowHq,
  onSaved,
  onClose,
}: {
  journal: Journal | null;
  initial: Draft;
  sites: Site[];
  allowHq: boolean;
  onSaved: (id: string) => void;
  onClose: () => void;
}) {
  const id = journal?.id ?? null;
  const [draft, setDraft] = useState<Draft>(initial);
  const type = draft.journalType;
  const meta = JOURNAL_TYPES[type];
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  /** 새 일지를 저장한 뒤 들고 있던 사진을 붙이는 중 — 그동안 [저장] 을 다시 누르면 같은 날 일지를 또 만들려다 막힌다. */
  const [attaching, setAttaching] = useState(false);
  /**
   * [저장] 이 무엇에 막혔나 — 한 번에 하나씩 알린다(사용자 지시 2026-10-23 「한번에 하지 말고」). 현장이 먼저:
   * 현장이 비면 현장 칸만 붉고, 현장을 고른 뒤 다시 막히면 그때 비어 있는 [필수] 칸들이 붉다. 채우면 바로 풀린다.
   */
  const [blocked, setBlocked] = useState<'site' | 'fields' | null>(null);
  const siteMissing = blocked === 'site' && !draft.site;
  const missing = blocked === 'fields' ? missingFields(draft) : NO_FIELDS;
  /**
   * 종이에 바로 쓰기(안전보건일지 · DOC-122 §13 · 사용자 요청 「미리보기 화면 자체에서 입력」). 켜면 칸 입력을
   * 접고 종이를 실제 크기로 넓혀 그 칸에 쓴다. 두 화면은 같은 값을 고친다 — 접은 칸 입력은 그대로 살아 있다.
   */
  // 순회점검 양식(V535 · V540) — 새 일지의 점검 항목과 종이 머리 글. 서버가 아직 옛 판이면(404) 화면 상수로 시작한 그대로 둔다.
  const template = usePatrolTemplate(isInspection(type));
  useEffect(() => {
    const items = template.data?.items;
    if (journal?.id || !isInspection(type) || !items || items.length === 0) return;
    // 이미 체크한 결과는 같은 항목이면 지킨다(양식이 늦게 왔을 때).
    setDraft((prev) => {
      const kept = new Map(prev.checks.map((row) => [`${row.category ?? ''}\u0000${row.item}`, row.result]));
      return { ...prev, checks: items.map((one) => ({ category: one.category, item: one.item, result: kept.get(`${one.category ?? ''}\u0000${one.item}`) ?? null })) };
    });
  }, [template.data, journal?.id, type]);
  const write = useSafeJournalWrite();
  const catalog = useEducationCatalog(type === 'EDUCATION');
  /** 마지막으로 기본값을 채운 교육(chooseEducation). 처음엔 열린 일지의 교육. */
  const filledFrom = useRef<EducationSub | null>(null);

  const patch = (next: Partial<Draft>) => {
    setDraft((prev) => ({ ...prev, ...next }));
    setDirty(true);
  };

  /**
   * 교육 종류를 고르면 그 종류의 양식 기본값이 들어온다(§16) — 교육 내용(근거 조항 줄 + 항목) · 교육시간 · 교육대상 ·
   * 강사 · 교육자료. 칸이 비어 있거나 <b>앞서 고른 종류의 기본값이 손대지 않은 채</b> 남아 있을 때만 바꾼다 — 사람이
   * 적은 글은 지킨다(사용자 결정 2026-10-23 「비어있을 때 채워」). 특별안전보건교육은 39종 중 하나를 골라야 들어온다.
   */
  const chooseEducation = (educationKind: EducationKind | null, educationSubKind: string | null) => {
    // 「앞서 고른 종류」 는 마지막으로 기본값을 채운 교육이다 — 특별교육처럼 대분류만 고른 사이(소분류 없음)를 거쳐도
    // 그 전 교육의 기본값이 손대지 않은 채인지 알아본다. 빠뜨리면 정기교육 문구가 특별교육 14 로 바꿔도 남는다.
    const before = filledFrom.current ?? educationSubOf(catalog.data, draft.educationKind, draft.educationSubKind);
    const after = educationSubOf(catalog.data, educationKind, educationSubKind);
    if (after) filledFrom.current = after;
    setDraft((prev) => {
      // 6번 칸에 적은 글(title)은 그 교육의 것이다 — 다른 교육으로 바꾸면 비운다.
      const title = educationKind === prev.educationKind ? prev.title : null;
      if (!after) return { ...prev, educationKind, educationSubKind, title };
      const follow = <T,>(current: T | null, old: T | null | undefined, next: T | null | undefined): T | null => {
        const empty = current === null || (typeof current === 'string' && !current.trim());
        return empty || current === (old ?? null) ? (next ?? null) : current;
      };
      return {
        ...prev,
        educationKind,
        educationSubKind,
        title,
        content: follow(prev.content, before?.content.join('\n'), after.content.join('\n')),
        educationHours: follow(prev.educationHours, before?.hours, after.hours),
        target: follow(prev.target, before?.target, after.target),
        instructor: follow(prev.instructor, before?.instructor, after.instructor),
        educationMaterial: follow(prev.educationMaterial, before?.material, after.material),
      };
    });
    setDirty(true);
  };
  const text = (value: string) => (value === '' ? null : value);

  /**
   * 저장 본문. 안전교육일지의 [필수] 칸(기존 사이트 화면)은 여기서 본다 — 서버는 대상 · 인원을 모르는 옛 앱도
   * 받아야 해서 막지 않는다. 화면에서 뺀 칸(제목 · 참석자 · 특이사항 …)은 받은 그대로 돌려보낸다.
   */
  const buildBody = (): JournalInput | null => {
    // 비어 있는 [필수] 칸은 글로 알리지 않고 그 칸에 빨간 테두리를 친다(사용자 지시 2026-10-23). 현장만 글로 알린다.
    if (!draft.site) {
      setBlocked('site');
      setError('현장을 고르세요.');
      return null;
    }
    if (missingFields(draft).size > 0) {
      setBlocked('fields');
      setError(null);
      return null;
    }
    setBlocked(null);
    const { site, ...rest } = draft;
    const educationMaterial = type === 'EDUCATION' ? (rest.educationMaterial ?? '') : rest.educationMaterial;
    return { ...rest, educationMaterial, siteId: site === HQ ? null : site };
  };

  /**
   * 저장 전에 고른 사진 — 현장 · 교육 정보보다 사진이 먼저 올 수 있다(사용자 지시 2026-10-23 「현장 정보 입력 전에 사진이
   * 들어갈 수 있어야」). 고르는 순간에는 이 창에만 들고 종이에 보여 주고, [저장] 으로 일지 id 를 받으면 그때 붙인다.
   */
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const hold: PhotoHold = {
    add: (files, slot) => {
      setPending((prev) => {
        const kept = slot ? prev.filter((one) => one.slot !== slot) : prev;
        const added = files.map((file) => ({
          id: `pending:${crypto.randomUUID()}`,
          file,
          url: URL.createObjectURL(file),
          slot,
          caption: slot ? TBM_CAPTION : null,
        }));
        return [...kept, ...added];
      });
      setDirty(true);
    },
    drop: (photoId) =>
      setPending((prev) => {
        const gone = prev.find((one) => one.id === photoId);
        if (gone) URL.revokeObjectURL(gone.url);
        return prev.filter((one) => one.id !== photoId);
      }),
    caption: (photoId, caption) => setPending((prev) => prev.map((one) => (one.id === photoId ? { ...one, caption } : one))),
  };
  const queryClient = useQueryClient();
  const photos: JournalPhoto[] = [
    ...(journal?.photos ?? []),
    ...pending.map((one, at) => ({
      id: one.id,
      fileName: one.file.name,
      shotAt: null,
      caption: one.caption,
      slot: one.slot,
      sortOrder: 10000 + at,
      url: one.url,
    })),
  ];

  /** 새 일지를 저장해 id 를 받은 뒤 — 들고 있던 사진을 붙인다. 하나가 실패해도 나머지는 붙인다. 다 붙었으면 true. */
  const attachPending = async (target: string): Promise<boolean> => {
    let ok = true;
    for (const one of pending) {
      try {
        const saved = await attachPhoto(type, target, one.file, { caption: one.caption, slot: one.slot });
        queryClient.setQueryData(safeJournalKeys.one(target), saved);
      } catch (cause) {
        ok = false;
        setError(errorMessage(cause));
      }
    }
    void queryClient.invalidateQueries({ queryKey: [...safeJournalKeys.all, 'list'] });
    return ok;
  };

  const loadPrevious = async () => {
    setError(null);
    setMessage(null);
    if (id) {
      setError('새 일지에서 불러올 수 있습니다.');
      return;
    }
    if (!draft.site) {
      setError('현장을 먼저 고르세요.');
      return;
    }
    if (dirty && !window.confirm('적어 둔 내용을 지난 일지로 바꿀까요?')) return;
    setLoadingPrevious(true);
    try {
      const previous = await fetchLatestSafeJournal(type, draft.site, draft.journalDate);
      if (!previous) {
        setMessage('불러올 지난 일지가 없습니다.');
        return;
      }
      setDraft((prev) => carryOver(prev, previous));
      setDirty(true);
      setMessage(`${previous.journalDate} 일지를 불러왔습니다.`);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoadingPrevious(false);
    }
  };

  /** [저장] — 끝나면 창을 닫는다(§23). */
  const save = () => {
    setError(null);
    const body = buildBody();
    if (!body) return;
    write.mutate(
      { id, body },
      {
        onSuccess: async (data) => {
          setDirty(false);
          let attached = true;
          if (data && !id && pending.length > 0) {
            setAttaching(true);
            attached = await attachPending(data.id);
          }
          // 사진이 못 붙었으면 닫지 않는다 — 저장된 일지로 바꿔 사유와 붙은 사진을 보여 준다.
          if (!attached && data) {
            onSaved(data.id);
            return;
          }
          onClose();
        },
        onError: (cause) => setError(errorMessage(cause)),
      },
    );
  };

  /**
   * 결재란 「현장소장」 승인(V548). 저장 전이거나 고친 게 있으면 먼저 저장한다 — 승인은 저장된 일지에 찍힌다
   * (고쳐 저장하면 승인이 풀리므로, 저장한 뒤에 승인해야 남는다). 창은 닫지 않는다.
   */
  const headApprove = useHeadApproval(id);
  const approveHead = async (approve: boolean, role: ApprovalRole) => {
    setError(null);
    if (!approve) {
      if (id) headApprove.mutate({ target: id, approve: false, role }, { onError: (cause) => setError(errorMessage(cause)) });
      return;
    }
    let target = id;
    if (!target || dirty) {
      const body = buildBody();
      if (!body) return;
      try {
        const saved = await write.mutateAsync({ id, body });
        setDirty(false);
        target = saved ? saved.id : null;
        if (target && !id && pending.length > 0) {
          setAttaching(true);
          await attachPending(target);
        }
      } catch (cause) {
        setError(errorMessage(cause));
        return;
      }
    }
    if (!target) return;
    const saved = target;
    headApprove.mutate(
      { target: saved, approve: true, role },
      {
        onSuccess: () => {
          if (!id) onSaved(saved);
        },
        onError: (cause) => setError(errorMessage(cause)),
      },
    );
  };

  const remove = () => {
    setError(null);
    if (!id) {
      onClose();
      return;
    }
    if (!window.confirm('이 일지를 삭제할까요? 줄과 사진이 함께 지워집니다.')) return;
    write.mutate({ id, body: null }, { onSuccess: onClose, onError: (cause) => setError(errorMessage(cause)) });
  };

  const exportPdf = async () => {
    setError(null);
    if (!id) {
      setError('저장한 뒤 내려받을 수 있습니다.');
      return;
    }
    if (dirty) {
      setError('고친 내용을 먼저 저장하세요.');
      return;
    }
    setDownloading(true);
    try {
      await downloadSafeJournal(id, `${meta.label}_${draft.journalDate}`);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setDownloading(false);
    }
  };

  const busy = write.isPending || attaching || downloading || loadingPrevious;
  /** 결재란 두 칸의 승인 손잡이 — 종이 · 표 한 장(위험성평가 · TBM 일지)이 함께 쓴다. */
  const approvals: Approvals = {
    HEAD: {
      value: journal?.headApproval ?? null,
      approve: () => void approveHead(true, 'HEAD'),
      cancel: () => void approveHead(false, 'HEAD'),
      busy: headApprove.isPending || write.isPending,
    },
    MANAGER: {
      value: journal?.managerApproval ?? null,
      approve: () => void approveHead(true, 'MANAGER'),
      cancel: () => void approveHead(false, 'MANAGER'),
      busy: headApprove.isPending || write.isPending,
    },
  };
  const siteName =
    draft.site === HQ ? '본사' : (sites.find((one) => one.id === draft.site)?.name ?? journal?.siteName ?? '');

  // 새 교육일지의 첫 걸음(종류 고르기) — 창을 내용 폭으로 좁히고, 종류를 고르면 종이 폭으로 넓힌다.
  const picking = type === 'EDUCATION' && !id && !draft.educationKind;

  return (
    <Modal
      title={`${meta.label} ${id ? '수정' : '작성'}`}
      onClose={onClose}
      maxWidth={picking ? '760px' : '1380px'}
      closeDisabled={write.isPending || attaching}
      beforeClose={() => !dirty || window.confirm('저장하지 않은 내용이 있습니다. 닫을까요?')}
      actions={
        <div className="flex items-center gap-2">
          <button className="btn-secondary" disabled={busy} onClick={() => void loadPrevious()}>
            {loadingPrevious ? '불러오는 중...' : '지난 일지'}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={exportPdf}>
            {downloading ? '만드는 중...' : 'PDF'}
          </button>
          <button className="btn-secondary" style={{ color: 'var(--danger)' }} disabled={busy} onClick={remove}>
            삭제
          </button>
          <button className="btn-primary" disabled={busy} onClick={() => save()}>
            {attaching ? '사진 올리는 중...' : write.isPending ? '저장 중...' : '저장'}
          </button>
        </div>
      }
    >
      <style>{DESK_STYLES}</style>
      {/*
        종이에 바로 쓴다(DOC-122 §17 · 사용자 결정 「깨끗한 종이 + 누르면 그 칸만 편집」). 종이 밖의 값 — 현장 · 날짜 ·
        교육 종류 · 점검자 — 은 종이 위 한 줄에서 고른다. 사진은 종이 아래에서 올린다.
      */}
      {/*
        새 일지는 교육 종류부터 크게 고른다 — 종류마다 종이 양식 · 내용이 통째로 달라서(사용자 2026-11-04 「기존에 있던 모달을 살리는 게
        낫지 않나」). 기존 일지는 종류가 비어 있어도(예: 고정 예시) 종이를 바로 연다 — 고르개가 먼저 뜨면 쓰던 글이 없어진 것처럼 보인다.
      */}
      {picking ? (
        <div style={{ padding: '4px 2px 8px' }} className="space-y-3">
          {error && <Notice tone="warning">{error}</Notice>}
          <EducationPicker catalog={catalog.data} kind={null} sub={null} onPick={(kind, sub) => chooseEducation(kind, sub)} />
        </div>
      ) : (
        <div className={`sj-desk ${isSheetType(type) ? 'sj-desk-sheet' : ''}`}>
          {/* 왼쪽 — 종이 밖의 값(현장 · 날짜 · 교육 종류 · 점검자) · 양식에 없는 위험요인 표 · 사진(§20). 나머지는 종이에 바로. */}
          <div className="sj-side">
            {error && <Notice tone="warning">{error}</Notice>}
            {message && (
              <Notice tone="info" closable onClose={() => setMessage(null)}>
                {message}
              </Notice>
            )}
            <div className="sj-panel">
              <div className="sj-tool">
                <span>현장</span>
                <SiteSearchSelect
                  sites={sites}
                  value={draft.site}
                  onChange={(site) => {
                    patch({ site });
                    if (site && blocked === 'site') {
                      setBlocked(null);
                      setError(null);
                    }
                  }}
                  extras={allowHq ? HQ_OPTION : []}
                  width="100%"
                  className="input"
                  buttonStyle={siteMissing ? { ...siteButton, ...MISSING_INPUT } : siteButton}
                  disabled={!!id}
                />
              </div>
              <div className="sj-tool">
                <span>
                  {type === 'EDUCATION' ? '교육 일시' : isInspection(type) ? '점검일' : type === 'RISK' ? '평가일' : type === 'TBM' ? '작업일자' : '작성일'}
                </span>
                <input
                  type="date"
                  className="input"
                  style={missing.has('journalDate') ? { ...full, ...MISSING_INPUT } : full}
                  value={draft.journalDate}
                  onChange={(e) => patch({ journalDate: e.target.value })}
                />
              </div>
              {type === 'EDUCATION' && (
                <>
                  {/* 대분류 · 소분류가 위 — 아래 검색 고르개가 펼쳐져도 밀리지 않는다. */}
                  <EducationKindSelects
                    catalog={catalog.data}
                    kind={draft.educationKind}
                    sub={draft.educationSubKind}
                    onPick={(kind, sub) => chooseEducation(kind, sub)}
                  />
                  <div className="sj-tool">
                    <span>교육 종류</span>
                    <EducationPicker
                      compact
                      catalog={catalog.data}
                      kind={draft.educationKind}
                      sub={draft.educationSubKind}
                      onPick={(kind, sub) => chooseEducation(kind, sub)}
                    />
                  </div>
                </>
              )}
              {isInspection(type) && (
                <div className="sj-tool">
                  <span>점검자</span>
                  <NameSuggestInput style={full} value={draft.inspector ?? ''} onChange={(v) => patch({ inspector: text(v) })} />
                </div>
              )}
            </div>
            <div className="sj-panel">
              <PhotoSection
                type={type}
                journalId={id}
                // 안전보건일지는 사진대지가 한 칸이 되어(§31) 옛 TBM 자리 사진도 여기서 함께 다룬다.
                photos={type === 'HEALTH' ? photos : photos.filter((one) => !one.slot)}
                hold={id ? null : hold}
                onError={setError}
              />
            </div>
            <div className="sj-panel">
              <SignRouteSection
                value={draft.signRoute ?? blankSignRoute()}
                onChange={(signRoute) => patch({ signRoute })}
                siteName={siteName}
                signers={
                  type === 'HEALTH' || isSheetType(type)
                    ? SHEET_SIGNERS
                    : isInspection(type)
                      ? template.data?.header?.signers?.length
                        ? template.data.header.signers
                        : ['소장']
                      : (educationSubOf(catalog.data, draft.educationKind, draft.educationSubKind)?.signers?.length ?? 0) === 2
                        ? educationSubOf(catalog.data, draft.educationKind, draft.educationSubKind)!.signers!
                        : ['안전관리자', '소장']
                }
              />
            </div>
          </div>
          {/* 오른쪽 — 종이. 한 칸짜리는 누르면 그 칸만 열린다(§17). */}
          <div className="sj-paper-scroll">
            {isSheetType(type) ? (
              isDocType(type) ? (
                <DocSheet type={type} journal={draft} edit={patch} approvals={approvals} />
              ) : type === 'RISK' ? (
                <RiskSheet journal={draft} edit={patch} approvals={approvals} />
              ) : (
                <TbmSheet journal={draft} siteId={draft.site || null} edit={patch} approvals={approvals} journalId={id} />
              )
            ) : (
            <JournalPaper
              form={DEFAULT_FORMS[type]}
              journal={draft}
              siteName={siteName}
              kindLabel={educationLabel(catalog.data, draft.educationKind, draft.educationSubKind)}
              photos={photos}
              edit={patch}
              patrolHeader={template.data?.header}
              photoUpload={<PhotoUploadButton type={type} journalId={id} hold={id ? null : hold} onError={setError} />}
              education={{
                sub: educationSubOf(catalog.data, draft.educationKind, draft.educationSubKind),
                boxes: catalog.data?.boxes ?? [],
                choose: (kind, sub) => chooseEducation(kind as EducationKind, sub),
                photoUpload: <PhotoUploadButton type={type} journalId={id} hold={id ? null : hold} onError={setError} />,
              }}
              missing={missing}
              approvals={approvals}
              slotTools={(slot) => {
                const photo = photos.find((one) => one.slot === slot) ?? null;
                const hand = { slot, journalId: id, photo, hold: id ? null : hold, onError: setError };
                return { upload: <SlotUploadButton {...hand} />, caption: <SlotCaption {...hand} /> };
              }}
            />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/** 현장 고르기 맨 위의 본사 — 현장 묶음(국내 진행 · 해외 진행 · 예정 · 평가 · 준공) 위에 선다. */
const HQ_OPTION = [{ value: HQ, label: '본사' }];
/** 고르기 단추를 옆의 select · 날짜 칸과 같은 높이로. */
const siteButton: CSSProperties = { ...inputStyle, minHeight: '38px' };

/**
 * 작성 창 — 왼쪽 패널(현장 · 날짜 · 종류 · 위험요인 표 · 사진)과 오른쪽 종이. 둘이 따로 스크롤한다. 좁은 창에서는 위아래로 쌓인다.
 */
const DESK_STYLES = `
.sj-desk { display: grid; grid-template-columns: 440px minmax(0, 1fr); gap: 16px; height: calc(90vh - 120px); }
.sj-side { overflow-y: auto; min-height: 0; display: flex; flex-direction: column; gap: 12px; padding-right: 4px; }
.sj-panel { display: flex; flex-direction: column; gap: 10px; background: var(--bg-card, #fff); border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px; }
.sj-tool { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.sj-tool > span { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
/* 다음에 고를 칸(교육 종류) — 새 일지를 열면 대분류, 고르면 소분류로 옮겨 간다. */
.sj-next > span { color: var(--accent-light); font-weight: 700; }
.sj-next select { border: 2px solid var(--accent-light) !important; background-color: var(--accent-bg) !important; color: var(--accent); font-weight: 600;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.25); animation: sj-next-pulse 1.4s ease-in-out infinite; }
@keyframes sj-next-pulse { 50% { box-shadow: 0 0 0 7px rgba(59, 130, 246, 0.12); } }
.sj-paper-scroll { overflow: auto; min-height: 0; background: var(--bg-muted, #f1f5f9); border-radius: 10px; padding: 16px; }
.sj-paper-scroll > div { margin: 0 auto; }
/* 위험성평가 · TBM 일지(표 한 장)는 표가 넓다 — 왼쪽 칸을 좁힌다. */
.sj-desk.sj-desk-sheet { grid-template-columns: 300px minmax(0, 1fr); }
@media (max-width: 1240px) {
  .sj-desk { grid-template-columns: minmax(0, 1fr); height: auto; }
  .sj-side, .sj-paper-scroll { overflow: visible; }
}
` + SHEET_STYLES;

type PhotoHand = { type: JournalType; journalId: string | null; hold: PhotoHold | null; onError: (message: string | null) => void };

/** 자리 없는 사진(교육 사진 · 그 밖의 사진)을 올린다. 저장한 일지는 바로 붙이고, 아직이면 들고 있다가 [저장] 때 붙인다. */
function usePhotoUpload({ type, journalId, hold, onError }: PhotoHand) {
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    onError(null);
    if (!journalId) {
      hold?.add(Array.from(files), null);
      return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const journal = await attachPhoto(type, journalId, file);
        queryClient.setQueryData(safeJournalKeys.one(journalId), journal);
      }
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setUploading(false);
      void queryClient.invalidateQueries({ queryKey: [...safeJournalKeys.all, 'list'] });
    }
  };
  return { uploading, handleFiles };
}

/** 종이의 「교육 사진」 칸 위 단추 — 여러 장을 고른다. 넷을 넘으면 사진대지 장으로 이어진다(§25). */
function PhotoUploadButton(hand: PhotoHand) {
  const { uploading, handleFiles } = usePhotoUpload(hand);
  return (
    <label className="btn-secondary" style={{ fontSize: '12px', padding: '4px 10px', cursor: 'pointer', background: '#fff' }}>
      {uploading ? '올리는 중...' : '＋ 사진'}
      <input
        type="file"
        accept="image/*"
        multiple
        hidden
        disabled={uploading}
        onChange={(e) => {
          void handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </label>
  );
}

/**
 * 대분류 · 소분류 — 기존 사이트 화면의 두 고르기(사용자 지시 2026-10-23 · PPT 「안전보건교육일지-①」). 이름은 법령의 긴
 * 이름 그대로이고 근거 조항을 붙인다: 소분류가 모두 같은 조항이면 대분류에(특별교육 · 제29조제3항), 다르면 소분류마다
 * (근로자 정기 제29조제1항 · 채용 시 제29조제2항). 고르면 위 검색 고르개와 같은 길 — 법정 교육 내용이 자동으로 들어간다.
 */
function EducationKindSelects({
  catalog,
  kind,
  sub,
  onPick,
}: {
  catalog: EducationCatalog | undefined;
  kind: EducationKind | null;
  sub: string | null;
  onPick: (kind: EducationKind, sub: string | null) => void;
}) {
  const kinds = catalog?.kinds ?? [];
  const subs = educationSubsOf(catalog, kind);
  // 다음에 고를 칸을 띄운다 — 대분류가 비었으면 대분류, 소분류가 남았으면 소분류.
  const next = !kind ? 'kind' : !sub && subs.length > 0 ? 'sub' : null;
  const kindRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (!kind) kindRef.current?.focus();
    // 처음 열릴 때 한 번만 — 고른 뒤에 다시 끌어오지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div className={`sj-tool ${next === 'kind' ? 'sj-next' : ''}`}>
        <span>대분류</span>
        <select
          ref={kindRef}
          className="input"
          style={full}
          value={kind ?? ''}
          onChange={(e) => {
            const next = e.target.value as EducationKind;
            const list = educationSubsOf(catalog, next);
            onPick(next, list.length === 1 ? list[0]!.code : null);
          }}
        >
          {/* 비어 있을 때 첫 항목이 골라진 것처럼 보이면 그것을 눌러도 바뀌지 않아 소분류가 잠긴 채 남는다. */}
          {!kind && <option value="">선택</option>}
          {kinds.map((one) => {
            return (
              <option key={one.code} value={one.code}>
                {plainLabel(one.label)}
              </option>
            );
          })}
        </select>
      </div>
      <div className={`sj-tool ${next === 'sub' ? 'sj-next' : ''}`}>
        <span>소분류</span>
        <select
          className="input"
          style={full}
          value={sub ?? ''}
          disabled={subs.length === 0}
          onChange={(e) => onPick(kind as EducationKind, e.target.value || null)}
        >
          {!sub && <option value="">{subs.length === 0 ? '-' : '고르세요'}</option>}
          {subs.map((one) => {

            return (
              <option key={one.code} value={one.code}>
                {plainLabel(kind === 'SPECIAL' ? (one.short ?? one.label) : one.label)}
              </option>
            );
          })}
        </select>
      </div>
    </>
  );
}

/**
 * 결재 요청(V550 · 사용자 지시 2026-10-23 「안전관리자, 소장 순으로 서명 — 작성되면 Todo · 푸시 알림, 설정할 수 있게」).
 * 결재 칸마다 사람을 주소록에서 고르고(그 현장 사람이 먼저 선다), 할일 · 앱 푸시 스위치를 고른다. 저장하면 차례가 온
 * 사람(다른 칸 → 소장 칸)에게 간다. 지정된 사람은 자기 칸을 승인할 수 있다.
 */
function SignRouteSection({
  value,
  onChange,
  siteName,
  signers,
}: {
  value: SignRoute;
  onChange: (next: SignRoute) => void;
  siteName: string;
  signers: string[];
}) {
  const directory = useDirectoryTree(true);
  const people = (directory.data ?? []).flatMap((unit) =>
    unit.members.map((member) => ({ id: member.personnelId, name: member.name, deptSite: unit.unitName })),
  );
  const candidates = (directory.data ?? [])
    .filter((unit) => unit.unitType === 'SITE' && unit.unitName === siteName)
    .flatMap((unit) => unit.members.map((member) => ({ id: member.personnelId, name: member.name, deptSite: unit.unitName })));
  const head = headCellOf(signers);
  const cells = signers.map((name, at) => ({ name, role: at === head ? ('HEAD' as const) : ('MANAGER' as const) }));
  const pick = (role: 'HEAD' | 'MANAGER', personnelId: string | null) => {
    const name = personnelId ? (people.find((one) => one.id === personnelId)?.name ?? null) : null;
    onChange(
      role === 'HEAD'
        ? { ...value, headPersonnelId: personnelId, headName: name }
        : { ...value, managerPersonnelId: personnelId, managerName: name },
    );
  };
  const label: CSSProperties = { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <span style={label}>결재 요청</span>
      {cells.map(({ name, role }) => {
        const chosenId = role === 'HEAD' ? value.headPersonnelId : value.managerPersonnelId;
        const chosenName = role === 'HEAD' ? value.headName : value.managerName;
        return (
          <div key={role} style={{ display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr)', gap: '8px', alignItems: 'start' }}>
            <span style={{ fontSize: '12px', paddingTop: '4px' }}>{name}</span>
            {chosenId ? (
              <span className="selector-chip active" style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {chosenName ?? '—'}
                <button
                  type="button"
                  aria-label={`${name} 지정 취소`}
                  onClick={() => pick(role, null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
                >
                  ✕
                </button>
              </span>
            ) : (
              <PersonnelSearchInput people={people} candidates={candidates} onPick={(personnelId) => pick(role, personnelId)} />
            )}
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: '14px', fontSize: '12px' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
          <input type="checkbox" checked={value.notifyTodo} onChange={(e) => onChange({ ...value, notifyTodo: e.target.checked })} />
          할일
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
          <input type="checkbox" checked={value.notifyPush} onChange={(e) => onChange({ ...value, notifyPush: e.target.checked })} />
          앱 푸시
        </label>
      </div>
    </div>
  );
}

/** 사진내용의 기본 문구 — 양식에 찍혀 있는 글이다. */
const TBM_CAPTION = '안전조회 및 TBM 실시';

/** 저장 전에 고른 사진 한 장 — 이 창에만 있다. id 는 「pending:」 으로 시작한다. */
type PendingPhoto = { id: string; file: File; url: string; slot: PhotoSlot | null; caption: string | null };

/** 저장 전 사진의 손잡이 — 저장한 일지에서는 null(바로 서버에 붙인다). */
type PhotoHold = {
  add: (files: File[], slot: PhotoSlot | null) => void;
  drop: (photoId: string) => void;
  caption: (photoId: string, caption: string | null) => void;
};

const isPending = (photo: JournalPhoto) => photo.id.startsWith('pending:');

/** 사진 한 장을 JPEG 로 줄여 올리고 일지에 붙인다 — PDF 렌더러가 WebP 를 못 그린다(공사일보와 같은 프로파일). */
async function attachPhoto(
  type: JournalType,
  target: string,
  file: File,
  extra: { caption?: string | null; slot?: PhotoSlot | null } = {},
): Promise<Journal> {
  const exif = parseExif(await file.arrayBuffer());
  const compressed = await compressToJpeg(file, DOCUMENT_PROFILE);
  const fileName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  const key = await uploadSafeJournalFile(type, compressed.blob, fileName);
  return addSafeJournalPhoto(target, {
    objectKey: key,
    fileName,
    shotAt: exif.shotAt ? exif.shotAt.toISOString() : null,
    ...extra,
  });
}

type SlotHand = {
  slot: PhotoSlot;
  journalId: string | null;
  photo: JournalPhoto | null;
  hold: PhotoHold | null;
  onError: (message: string | null) => void;
};

/** 사진대지 한 자리에 사진을 올린다. 아직 저장 안 한 일지면 들고 있다가 저장할 때 붙인다. */
function useSlotUpload({ slot, journalId, photo, hold, onError }: SlotHand) {
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();
  const upload = async (file: File | undefined) => {
    if (!file) return;
    onError(null);
    if (!journalId) {
      hold?.add([file], slot);
      return;
    }
    setUploading(true);
    try {
      const journal = await attachPhoto('HEALTH', journalId, file, { caption: photo?.caption ?? TBM_CAPTION, slot });
      queryClient.setQueryData(safeJournalKeys.one(journalId), journal);
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setUploading(false);
      void queryClient.invalidateQueries({ queryKey: [...safeJournalKeys.all, 'list'] });
    }
  };
  return { uploading, upload };
}

/** 종이의 사진대지 칸 위 단추 — 누르면 사진을 고른다. */
function SlotUploadButton(hand: SlotHand) {
  const { uploading, upload } = useSlotUpload(hand);
  return (
    <label className="btn-secondary" style={{ fontSize: '12px', padding: '4px 10px', cursor: 'pointer', background: '#fff' }}>
      {uploading ? '올리는 중...' : hand.photo ? '사진 바꾸기' : '＋ 사진'}
      <input
        type="file"
        accept="image/*"
        hidden
        disabled={uploading}
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </label>
  );
}

/** 종이의 사진내용 칸 — 사진이 있을 때 고치고, 칸을 떠날 때 저장한다. */
function SlotCaption({ journalId, photo, hold, onError }: SlotHand) {
  const photoWrite = useSafeJournalPhotoWrite(journalId);
  if (!photo) return <>{TBM_CAPTION}</>;
  return (
    <input
      key={photo.id}
      style={{ width: '100%', border: 'none', font: 'inherit', background: 'rgba(37, 99, 235, 0.06)' }}
      defaultValue={photo.caption ?? TBM_CAPTION}
      onBlur={(e) => {
        const caption = e.target.value.trim() || null;
        if (caption === (photo.caption ?? null)) return;
        if (isPending(photo)) {
          hold?.caption(photo.id, caption);
          return;
        }
        photoWrite.mutate({ kind: 'caption', photoId: photo.id, body: { caption } }, { onError: (cause) => onError(errorMessage(cause)) });
      }}
    />
  );
}

/**
 * 사진 — 일지 id 에 붙는다. 저장한 일지는 올리는 즉시 서버에 붙고(저장 버튼과 무관), 설명은 칸을 떠날 때 저장한다.
 * 아직 저장 안 한 일지는 {@code hold} 가 들고 있다가 [저장] 할 때 붙인다 — 사진을 먼저 고르고 현장 · 교육 정보는
 * 나중에 적어도 된다.
 */
function PhotoSection({
  type,
  journalId,
  photos,
  hold,
  onError,
}: {
  type: JournalType;
  journalId: string | null;
  photos: JournalPhoto[];
  hold: PhotoHold | null;
  onError: (message: string | null) => void;
}) {
  const photoWrite = useSafeJournalPhotoWrite(journalId);
  const { uploading, handleFiles } = usePhotoUpload({ type, journalId, hold, onError });

  return (
    <div>
      <div className="flex items-center justify-end" style={{ marginBottom: '6px' }}>
        <label className="btn-secondary" style={{ fontSize: '12px', padding: '4px 10px', cursor: 'pointer' }}>
          {uploading ? '올리는 중...' : '＋ 사진 추가'}
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            disabled={uploading}
            onChange={(e) => {
              void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
          {photos.map((photo) => (
            <div key={photo.id} className="space-y-1">
              <div style={{ position: 'relative' }}>
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt={photo.caption ?? photo.fileName ?? ''}
                    style={{ width: '100%', height: '130px', objectFit: 'cover', borderRadius: '8px', display: 'block' }}
                  />
                ) : (
                  <div style={{ height: '130px', borderRadius: '8px', background: 'var(--bg-muted, #f1f5f9)' }} />
                )}
                <button
                  type="button"
                  aria-label="사진 삭제"
                  disabled={photoWrite.isPending}
                  onClick={() => {
                    if (!window.confirm('이 사진을 뺄까요?')) return;
                    if (isPending(photo)) {
                      hold?.drop(photo.id);
                      return;
                    }
                    photoWrite.mutate({ kind: 'remove', photoId: photo.id }, { onError: (cause) => onError(errorMessage(cause)) });
                  }}
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    border: 'none',
                    borderRadius: '999px',
                    width: '22px',
                    height: '22px',
                    cursor: 'pointer',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                  }}
                >
                  ✕
                </button>
              </div>
              <input
                className="input"
                style={cellInput}
                defaultValue={photo.caption ?? ''}
                aria-label="사진 설명"
                onBlur={(e) => {
                  const caption = e.target.value.trim() || null;
                  if (caption === (photo.caption ?? null)) return;
                  if (isPending(photo)) {
                    hold?.caption(photo.id, caption);
                    return;
                  }
                  photoWrite.mutate({ kind: 'caption', photoId: photo.id, body: { caption } }, { onError: (cause) => onError(errorMessage(cause)) });
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
